import type { DyspatchEvent } from '@dyspatch/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, storedActor } from '@/lib/api';
import { EVENT_KINDS } from '@/lib/event-kinds';
import type { FeedItem } from '@/lib/feed';
import { FLEET_KEYS, MY_KEYS } from '@/lib/query';

/** REDESIGN-2 section 5: the rail holds 60 items and drops the rest. */
export const FEED_CAP = 60;

export type LiveState = 'connecting' | 'live' | 'reconnecting';

function toItem(event: DyspatchEvent, fresh: boolean): FeedItem {
  return {
    id: event.id,
    kind: event.kind ?? 'event',
    message: event.message ?? '',
    actor: event.actor ?? '',
    at: event.createdAt ?? new Date().toISOString(),
    subject: event.taskId ?? event.workerId ?? event.briefId ?? '',
    fresh,
  };
}

/**
 * The live wire.
 *
 * Two sources, in this order, and the order is the whole point: a REST read seeds
 * the rail with what already happened, then `EventSource` keeps it current. The
 * stream only ever sends what happens *after* it connects, so a page opened on a
 * quiet fleet would otherwise show an empty rail and look broken (HANDOFF §8).
 * Deduplication is by event id, so the seed and the stream overlapping during
 * connect costs nothing.
 *
 * Every frame also nudges the queries the events can have moved, debounced: one
 * brief creating fifteen tasks is one refresh, not fifteen.
 *
 * One hook serves both wires because they differ in three values and nothing
 * else: which URL, which seed read, and which keys a frame invalidates. What they
 * do not differ in is that both are streams -- a member's live state used to be a
 * 30s poll, and that is the gap this closed.
 */
function useEventStream(options: {
  enabled: boolean;
  path: string;
  seed: () => Promise<DyspatchEvent[]>;
  keys: readonly (readonly unknown[])[];
}): { items: FeedItem[]; live: LiveState } {
  const { enabled, path, seed, keys } = options;
  const [items, setItems] = useState<FeedItem[]>([]);
  const [live, setLive] = useState<LiveState>('connecting');
  const seen = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const push = useCallback((event: DyspatchEvent, fresh: boolean): boolean => {
    if (!event?.id || seen.current.has(event.id)) return false;
    seen.current.add(event.id);
    setItems((previous) => {
      const next = [toItem(event, fresh), ...previous].slice(0, FEED_CAP);
      for (const dropped of previous.slice(FEED_CAP - 1)) seen.current.delete(dropped.id);
      return next;
    });
    return true;
  }, []);

  useEffect(() => {
    /**
     * A different wire is a different journal.
     *
     * Signing in as somebody else on the token path changes the URL under this
     * effect, and without the reset the previous person's lines stay on the rail
     * while their ids go on suppressing the same events arriving on the new
     * stream. The seed that follows refills it.
     */
    setItems([]);
    seen.current.clear();
    if (!enabled) return;
    let closed = false;
    let nudge: ReturnType<typeof setTimeout> | null = null;

    const refreshSoon = () => {
      if (nudge) return;
      nudge = setTimeout(() => {
        nudge = null;
        for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
      }, 250);
    };

    const seedRail = async () => {
      try {
        const events = await seed();
        if (closed) return;
        // Newest first on the wire; replay oldest first so the unshift above
        // rebuilds that order rather than inverting it.
        for (const event of [...events].reverse()) push(event, false);
      } catch {
        // The rail is decoration over the lists. A failed seed is not a failed page.
      }
    };

    const source = new EventSource(path);

    const onFrame = (message: MessageEvent<string>) => {
      setLive('live');
      let parsed: DyspatchEvent | null = null;
      try {
        parsed = JSON.parse(message.data) as DyspatchEvent;
      } catch {
        parsed = null;
      }
      if (parsed) push(parsed, true);
      refreshSoon();
    };

    const onAlive = () => setLive('live');
    source.addEventListener('open', onAlive);
    source.addEventListener('ready', onAlive);
    source.addEventListener('ping', onAlive);
    // EventSource reconnects on its own; the amber state says so rather than
    // pretending the fleet went quiet.
    source.addEventListener('error', () => setLive('reconnecting'));
    source.addEventListener('message', onFrame);
    for (const kind of EVENT_KINDS) source.addEventListener(kind, onFrame);

    void seedRail();

    return () => {
      closed = true;
      if (nudge) clearTimeout(nudge);
      source.close();
    };
  }, [enabled, keys, path, push, queryClient, seed]);

  return { items, live };
}

/** Module-level so the effect's dependencies are stable across renders. */
const seedFleet = async () => (await api.events(FEED_CAP)).events;
const seedMine = async () => (await api.myEvents(FEED_CAP)).events;

/** The whole organisation's journal. Admin-only, on the server as well as here. */
export function useActivityFeed(enabled: boolean) {
  return useEventStream({ enabled, path: '/api/stream', seed: seedFleet, keys: FLEET_KEYS });
}

/**
 * A member's own wire: `GET /api/me/stream`, scoped in SQL to the devices they
 * own and the briefs they filed.
 *
 * The actor rides in the query string on the legacy admin-token path, because
 * `EventSource` cannot send the `x-dyspatch-actor` header every other request
 * carries and the hub would otherwise scope the stream to nobody. A signed-in
 * browser stores no actor, sends none, and is named by its session instead.
 */
export function useMyActivityFeed(enabled: boolean) {
  const actor = storedActor();
  const path = actor ? `/api/me/stream?actor=${encodeURIComponent(actor)}` : '/api/me/stream';
  return useEventStream({ enabled, path, seed: seedMine, keys: MY_KEYS });
}
